import { useCallback, useContext, useMemo } from 'react';
import {
  BusinessCollectionCompType,
  IObjektVy,
} from '../../../../../Types/ObjektVy';
import { collectionCompToObjektVy } from '../../../_Common/Components/BusinessCollectionComponents/BusinessCollectionUtils';
import { useSelectedDetailQuery } from '../../../../../Components/QueryComponents/SelectedDetailQuery/useSelectedDetailQuery';
import {
  LeverantorAttestQuery,
  LeverantorAttestQueryVariables,
  LeverantorAttestQuery_node_TEDLRLeverantorAttest,
} from './Queries/_QueryTypes/LeverantorAttestQuery';
import leverantorAttestQuery from './Queries/LeverantorAttestQuery.gql';
import ObjektVy from '../../../_Common/Components/BusinessCollectionComponents/ObjektVy/ObjektVy';
import BusinessDataForm, {
  BusinessDataFormContext,
  IDefinedBusinessDataFormContext,
} from '../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { IClientBusinessModel } from '../../../../../Types/ClientModelTypes';
import clientAvenyModel from '../../../../../Model/testClientAvenyModel';
import ObjektVySida from '../../../_Common/Components/BusinessCollectionComponents/ObjektVySida/ObjektVySida';
import AttributGrupp from '../../../_Common/Components/BusinessCollectionComponents/AttributGrupp/AttributGrupp';
import Attribut from '../../../_Common/Components/BusinessCollectionComponents/Attribut/Attribut';
import AttributeHeader from '../../../../../Components/Attributes/AttributeHeader/AttributeHeader';
import { ObjektVyKort } from '../../../_Common/Components/BusinessCollectionComponents/ObjektVyKort/ObjektVyKort';
import { AssociationList } from '../../../_Common/Components/BusinessCollectionComponents/AssociationList/AssociationList';
import { selectedId } from '../../../../../Components/MasterDetail/MasterDetailUtils';
import UpdatedMutation from './Mutations/LeverantorAttestUpdateMutation.gql';
import { useMutation } from '@apollo/client';
import {
  LeverantorAttestUpdateMutation,
  LeverantorAttestUpdateMutationVariables,
} from './Mutations/_QueryTypes/LeverantorAttestUpdateMutation';
import { handleGQLError } from '../../../../../Utils/Error';
import {
  DisabledType,
  IEditMenuAction,
  IEditMenuActionParams,
  IFormData,
  removeIdsFromNewAssociations,
} from '../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import { ActiveDatabaseContext } from '../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import EDTooltip from '../../../../../BaseComponents/Tooltip/EDTooltip';
import EDTypography from '../../../../../BaseComponents/Typography/EDTypography';
import { TEDLRLeverantorAttestSTATUSEnum } from '../../../../../globalQueryTypes';
import { mergeBusinessDataMutations } from '../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/MergeBusinessDataMutations';
import {
  FormUpdateAssociatonType,
  getFormUpdateValue,
} from '../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormValueUtils';
import EDAvenyProgress from '../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import EDKeybind from '../../../../../BaseComponents/Keybinds/EDKeybind';
import { LeverantorAttestWrapperContext } from '../../Rutiner/LeverantorAttest/Contexts/LeverantorAttestWrapperContext';
import DisplayText from '../../../../../Components/Attributes/DisplayText/DisplayText';
import EDCaptionedValue from '../../../../../BaseComponents/CaptionedValue/EDCaptionedValue';
import { TEDFormatDefault } from '../../../../../Utils/Formatters/FormatterClasses/TEDFormatDefault';
import EDBox from '../../../../../BaseComponents/EDBox/EDBox';
import { createAttestText } from './Utils/LeverantorAttestUtils';

const leverantorAttestModel: IClientBusinessModel =
  clientAvenyModel.LR.BusinessModels.LRLeverantorAttest;

const leverantormodel: IClientBusinessModel =
  clientAvenyModel.LR.BusinessModels.LRLeverantor;

const LeverantorAttest: BusinessCollectionCompType = ({ id }) => {
  const { activeDatabase, userID } = useContext(ActiveDatabaseContext);
  const { routineIsReadOnly } = useContext(LeverantorAttestWrapperContext);
  const {
    nodeData,
    loading: nodeLoading,
    refetch,
    error: leverantorAttestError,
  } = useSelectedDetailQuery<
    LeverantorAttestQuery,
    LeverantorAttestQuery_node_TEDLRLeverantorAttest,
    LeverantorAttestQueryVariables
  >(leverantorAttestQuery, id, {
    queryOptions: {
      onError: handleGQLError,
    },
  });

  const [mutation, { loading: mutationLoading, error: mutationError }] =
    useMutation<
      LeverantorAttestUpdateMutation,
      LeverantorAttestUpdateMutationVariables
    >(UpdatedMutation, {
      onError: handleGQLError,
    });

  const updateFunction = useCallback(
    (values: IFormData) => {
      const newValues = removeIdsFromNewAssociations(values ?? {});
      return mutation({
        variables: {
          db: activeDatabase || '',
          values: {
            id: nodeData?.id ?? '',
            _version: nodeData?._version ?? 1,
            ...newValues,
          },
        },
      });
    },
    [mutation, activeDatabase, nodeData?.id, nodeData?._version]
  );

  const isAttestButtonDisabled = useCallback(
    (params: IEditMenuActionParams): DisabledType => {
      const status = params.businessDataFormContext.data?.STATUS;
      const attesterat =
        params.businessDataFormContext.data?.ATTEST_TIMESTAMP !== null &&
        params.businessDataFormContext.data?._Associations?.AttestUser?.id ===
          userID;
      const attesteratAsSecondUser =
        params.businessDataFormContext.data?.ANDRA_ATTEST_TIMESTAMP !== null &&
        params.businessDataFormContext.data?._Associations?.AndraAttestUser
          ?.id === userID;

      if (attesterat || attesteratAsSecondUser) {
        return 'Du har redan attesterat denna leverantörsattest';
      }

      return status !== TEDLRLeverantorAttestSTATUSEnum.SKA_ATTESTERAS
        ? 'Fel status för attestering'
        : false;
    },
    [userID]
  );

  const handleAttesteraClick = useCallback(
    (params: IEditMenuActionParams) => {
      const data = params.businessDataFormContext.data as any;
      const primaryAttestExists =
        Boolean(data?._Associations?.AttestUser?.id) &&
        Boolean(data?.ATTEST_TIMESTAMP);

      const updateValues: FormUpdateAssociatonType[][] = [
        [
          {
            operation: 'OwnFields',
            values: {
              ...(primaryAttestExists
                ? { ANDRA_ATTEST_TIMESTAMP: new Date().toLocaleString('sv-SE') }
                : { ATTEST_TIMESTAMP: new Date().toLocaleString('sv-SE') }),
            },
          },
        ],
        [
          {
            associationName: primaryAttestExists
              ? 'AndraAttestUser'
              : 'AttestUser',
            operation: 'Ref',
            id: userID,
          },
        ],
      ];
      const mergedValues = mergeBusinessDataMutations(
        params.businessDataFormContext.formState.values ?? {},
        getFormUpdateValue(
          updateValues,
          params.businessDataFormContext.data ?? {}
        )
      );
      params.businessDataFormContext.saveForm(mergedValues);
    },
    [userID]
  );

  const editMenuButtonAttestera: IEditMenuAction = useMemo(
    () => ({
      id: 'attestera',
      isDisabled: isAttestButtonDisabled,
      label: (
        <EDTooltip
          title="Alt+A"
          enableClickOnTouch={Boolean(isAttestButtonDisabled)}
        >
          <EDTypography component="span" color="inherit" variant="button">
            <EDTypography
              underlined
              color="inherit"
              component="span"
              variant="button"
            >
              A
            </EDTypography>
            ttestera
          </EDTypography>
        </EDTooltip>
      ),
      shortLabel: 'Attestera',
      onClick: handleAttesteraClick,
      primary: true,
      icon: 'check_circle_outline',
      hideIconIfNotFloating: true,
    }),
    [isAttestButtonDisabled, handleAttesteraClick]
  );

  const handleAttestera = useCallback(
    (businessDataFormContext: IDefinedBusinessDataFormContext<any, unknown>) =>
      (e: React.KeyboardEvent) => {
        e.preventDefault();
        const params: IEditMenuActionParams = {
          businessDataFormContext,
          editMenuOnCancel: undefined,
        };
        const disabled = isAttestButtonDisabled(params);
        if (!disabled) {
          handleAttesteraClick(params);
        }
      },
    [isAttestButtonDisabled, handleAttesteraClick]
  );

  const inNeedOfSecondAttest = useMemo(() => {
    return (
      (nodeData?.STATUS === TEDLRLeverantorAttestSTATUSEnum.SKA_ATTESTERAS &&
        Boolean(nodeData?.ATTEST_TIMESTAMP) &&
        !nodeData?.ANDRA_ATTEST_TIMESTAMP) ||
      nodeData?._Associations?.AndraAttestUser != null
    );
  }, [
    nodeData?.STATUS,
    nodeData?.ATTEST_TIMESTAMP,
    nodeData?.ANDRA_ATTEST_TIMESTAMP,
    nodeData?._Associations?.AndraAttestUser,
  ]);

  return (
    <BusinessDataForm<LeverantorAttestQuery_node_TEDLRLeverantorAttest>
      dataLoading={nodeLoading}
      model={leverantorAttestModel}
      data={nodeData}
      refetchData={refetch}
      updateMutation={updateFunction}
      mutationError={mutationError}
      mutationLoading={mutationLoading}
      editMenuActions={[editMenuButtonAttestera]}
      disableEditMode={routineIsReadOnly}
    >
      <BusinessDataFormContext.Consumer>
        {businessDataFormContext =>
          businessDataFormContext && !routineIsReadOnly ? (
            <EDKeybind
              global
              alt
              keymap="a"
              onKeyDown={handleAttestera(businessDataFormContext)}
            />
          ) : null
        }
      </BusinessDataFormContext.Consumer>
      <ObjektVy nodeLoading={nodeLoading} error={leverantorAttestError}>
        <ObjektVySida
          caption="Leverantörsattest"
          collectionName={leverantorAttestModel.CollectionName}
          sidNr={1}
        >
          {(nodeLoading || mutationLoading) && <EDAvenyProgress />}
          <AttributGrupp readOnly>
            <AttributeHeader
              attributeGroups={['Leverantor.LeverantorNrNamn']}
            />
            <Attribut name="STATUS" size="small" />
            <EDBox
              sx={theme => ({
                padding: `${theme.spacing(1)} ${theme.spacing(1)} 0`,
              })}
            >
              <EDCaptionedValue
                label={'Attesterad av'}
                name={'Attesterad av'}
                fillContainer
              >
                <DisplayText
                  value={createAttestText(
                    nodeData?._Associations?.AttestUser?.Fullname,
                    nodeData?.ATTEST_TIMESTAMP
                  )}
                  displayer={new TEDFormatDefault().displayer}
                  noValueChar="—"
                />
              </EDCaptionedValue>
            </EDBox>

            {inNeedOfSecondAttest && (
              <EDBox
                sx={theme => ({
                  padding: `${theme.spacing(1)} ${theme.spacing(1)} 0`,
                })}
              >
                <EDCaptionedValue
                  label={'Ytterligare Attesterad av'}
                  name={'Ytterligare Attesterad av'}
                  fillContainer
                >
                  <DisplayText
                    value={createAttestText(
                      nodeData?._Associations?.AndraAttestUser?.Fullname,
                      nodeData?.ANDRA_ATTEST_TIMESTAMP
                    )}
                    displayer={new TEDFormatDefault().displayer}
                    noValueChar="—"
                  />
                </EDCaptionedValue>
              </EDBox>
            )}
          </AttributGrupp>
          <ObjektVyKort
            cardFullHeight
            caption="Händelser"
            readOnly
            hideHeaderMenu
          >
            <AssociationList
              associationName="Logg"
              collectionName="LRLeverantorAttest"
              associationPath="Logg"
              ownId={selectedId(id)}
              config={{
                defaultVisningstyp: 'lista',
                hideDefaultFunktionsMenyItems: true,
                inCard: true,
                integrated: true,
                customBusinessConfig: {
                  defaultSort: [
                    { attributeName: 'LOGG_TIMESTAMP', order: 'desc' },
                  ],
                },
              }}
            />
          </ObjektVyKort>
          {nodeData?.STATUS ===
            TEDLRLeverantorAttestSTATUSEnum.SKA_ATTESTERAS && (
            <ObjektVyKort
              cardFullHeight
              caption="Aktuella fakturor"
              readOnly
              hideHeaderMenu
            >
              <AssociationList
                associationPath="Leverantor.Fakturor"
                associationName="Fakturor"
                collectionName={leverantormodel.CollectionName}
                ownId={selectedId(id)}
                config={{
                  defaultVisningstyp: 'tabell',
                  hideDefaultFunktionsMenyItems: false,
                  inCard: true,
                }}
              />
            </ObjektVyKort>
          )}
        </ObjektVySida>
      </ObjektVy>
    </BusinessDataForm>
  );
};

const LeverantorAttestObjektVy = collectionCompToObjektVy(
  LeverantorAttest,
  'LeverantorAttest'
);

export const LeverantorAttestVyer: IObjektVy[] = [LeverantorAttestObjektVy];
